import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { authAPI } from '../lib/api';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(!!localStorage.getItem('apfx_admin_token'));

  const fetchMe = useCallback(async () => {
    const t = localStorage.getItem('apfx_token');
    if (!t) { setUser(null); setLoading(false); return; }
    try {
      const u = await authAPI.me();
      setUser(u);
      localStorage.setItem('apfx_user', JSON.stringify(u));
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from Google OAuth — skip the /me check so AuthCallback
    // can exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) { setLoading(false); return; }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { token, user } = await authAPI.login(email, password);
    localStorage.setItem('apfx_token', token);
    localStorage.setItem('apfx_user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const applySession = (token, u) => {
    localStorage.setItem('apfx_token', token);
    localStorage.setItem('apfx_user', JSON.stringify(u));
    setUser(u);
  };

  const register = async (email, password, name, referralCode) => {
    const { token, user } = await authAPI.register(email, password, name, referralCode);
    localStorage.setItem('apfx_token', token);
    localStorage.setItem('apfx_user', JSON.stringify(user));
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('apfx_token');
    localStorage.removeItem('apfx_user');
    setUser(null);
  };

  const refresh = fetchMe;

  const impersonate = (token, u) => {
    const adminTok = localStorage.getItem('apfx_token');
    if (adminTok) localStorage.setItem('apfx_admin_token', adminTok);
    localStorage.setItem('apfx_token', token);
    localStorage.setItem('apfx_user', JSON.stringify(u));
    setUser(u);
    setImpersonating(true);
  };

  const exitImpersonation = async () => {
    const adminTok = localStorage.getItem('apfx_admin_token');
    if (adminTok) { localStorage.setItem('apfx_token', adminTok); localStorage.removeItem('apfx_admin_token'); }
    setImpersonating(false);
    await fetchMe();
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh, setUser, applySession, impersonating, impersonate, exitImpersonation }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, impersonating]
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
